import { collection, query, getDocs, orderBy, doc, getDoc, where, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { firestore } from "../backend/firebaseConfig";

// Function to fetch all rides with rider profile images
export async function fetchAllRides() {
  try {
    const ridesCollection = collection(firestore, "Rides");
    const usersCollection = collection(firestore, "users");
    
    const q = query(ridesCollection, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    const ridesWithProfiles = await Promise.all(
      querySnapshot.docs.map(async (rideDoc) => {
        const rideData = rideDoc.data();
        
        let profileImage = "/Images/default-profile.jpeg";
        try {
          const userDocRef = doc(usersCollection, rideData.riderId);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            profileImage = userDoc.data().profileImage || profileImage;
          }
        } catch (userError) {
          console.error(`Error fetching user ${rideData.riderId}:`, userError);
        }
        
        return {
          id: rideDoc.id,
          ...rideData,
          profileImage
        };
      })
    );
    
    console.log("Fetched Rides with Profiles: ", ridesWithProfiles);
    return ridesWithProfiles;
  } catch (error) {
    console.error("Error fetching rides: ", error);
    throw error;
  }
}

// Function to fetch ride requests for a specific user (Admin view)
export async function fetchRideRequestsByUserId(userId) {
  try {
    const ridesRequestsCollection = collection(firestore, "RidesRequests");
    const usersCollection = collection(firestore, "users");
    
    const q = query(
      ridesRequestsCollection, 
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    
    const querySnapshot = await getDocs(q);
    
    const rideRequests = await Promise.all(
      querySnapshot.docs.map(async (requestDoc) => {
        const requestData = requestDoc.data();
        
        // Get rider profile data
        let riderProfile = null;
        try {
          const riderDocRef = doc(usersCollection, requestData.riderId);
          const riderDoc = await getDoc(riderDocRef);
          
          if (riderDoc.exists()) {
            const riderData = riderDoc.data();
            riderProfile = {
              profileImage: riderData.profileImage || "/Images/default-profile.jpeg",
              fullname: riderData.fullname,
              userType: riderData.userType
            };
          }
        } catch (userError) {
          console.error(`Error fetching rider ${requestData.riderId}:`, userError);
        }
        
        return {
          id: requestDoc.id,
          ...requestData,
          riderProfile
        };
      })
    );
    
    console.log("Fetched Ride Requests: ", rideRequests);
    return rideRequests;
  } catch (error) {
    console.error("Error fetching ride requests: ", error);
    throw error;
  }
}

// Function to fetch all ride requests (Admin overview)
export async function fetchAllRideRequests() {
  try {
    const ridesRequestsCollection = collection(firestore, "RidesRequests");
    const usersCollection = collection(firestore, "users");
    const ridesCollection = collection(firestore, "Rides");
    
    const q = query(ridesRequestsCollection, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    const rideRequests = await Promise.all(
      querySnapshot.docs.map(async (requestDoc) => {
        const requestData = requestDoc.data();
        
        // Get user profile data
        let userProfile = null;
        try {
          const userDocRef = doc(usersCollection, requestData.userId);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            userProfile = {
              profileImage: userData.profileImage || "/Images/default-profile.jpeg",
              fullname: userData.fullname,
              userType: userData.userType,
              email: userData.email
            };
          }
        } catch (error) {
          console.error(`Error fetching user ${requestData.userId}:`, error);
        }
        
        // Get rider profile data
        let riderProfile = null;
        try {
          const riderDocRef = doc(usersCollection, requestData.riderId);
          const riderDoc = await getDoc(riderDocRef);
          
          if (riderDoc.exists()) {
            const riderData = riderDoc.data();
            riderProfile = {
              profileImage: riderData.profileImage || "/Images/default-profile.jpeg",
              fullname: riderData.fullname,
              userType: riderData.userType,
              email: riderData.email
            };
          }
        } catch (error) {
          console.error(`Error fetching rider ${requestData.riderId}:`, error);
        }
        
        // Get ride details
        let rideDetails = null;
        try {
          const rideDocRef = doc(ridesCollection, requestData.rideId);
          const rideDoc = await getDoc(rideDocRef);
          
          if (rideDoc.exists()) {
            rideDetails = {
              id: rideDoc.id,
              ...rideDoc.data()
            };
          }
        } catch (error) {
          console.error(`Error fetching ride ${requestData.rideId}:`, error);
        }
        
        return {
          id: requestDoc.id,
          ...requestData,
          userProfile,
          riderProfile,
          rideDetails
        };
      })
    );
    
    console.log("Fetched All Ride Requests: ", rideRequests);
    return rideRequests;
  } catch (error) {
    console.error("Error fetching all ride requests: ", error);
    throw error;
  }
}

// Function to update ride request status (Admin action)
export async function updateRideRequestStatus(requestId, newStatus) {
  try {
    const ridesRequestsCollection = collection(firestore, "RidesRequests");
    const requestDocRef = doc(ridesRequestsCollection, requestId);
    
    await updateDoc(requestDocRef, {
      status: newStatus,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Ride request ${requestId} status updated to ${newStatus}`);
    return { success: true };
  } catch (error) {
    console.error("Error updating ride request status: ", error);
    throw error;
  }
}

// Function to delete ride request (Admin action)
export async function deleteRideRequest(requestId) {
  try {
    const ridesRequestsCollection = collection(firestore, "RidesRequests");
    const requestDocRef = doc(ridesRequestsCollection, requestId);
    
    await deleteDoc(requestDocRef);
    
    console.log(`Ride request ${requestId} deleted`);
    return { success: true };
  } catch (error) {
    console.error("Error deleting ride request: ", error);
    throw error;
  }
}

// Function to update ride (Admin action)
export async function updateRide(rideId, updateData) {
  try {
    const ridesCollection = collection(firestore, "Rides");
    const rideDocRef = doc(ridesCollection, rideId);
    
    await updateDoc(rideDocRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Ride ${rideId} updated successfully`);
    return { success: true };
  } catch (error) {
    console.error("Error updating ride: ", error);
    throw error;
  }
}

// Function to delete ride (Admin action)
export async function deleteRide(rideId) {
  try {
    const ridesCollection = collection(firestore, "Rides");
    const ridesRequestsCollection = collection(firestore, "RidesRequests");
    
    // Delete the ride
    const rideDocRef = doc(ridesCollection, rideId);
    await deleteDoc(rideDocRef);
    
    // Delete all associated ride requests
    const requestsQuery = query(ridesRequestsCollection, where("rideId", "==", rideId));
    const requestsSnapshot = await getDocs(requestsQuery);
    
    const deletePromises = requestsSnapshot.docs.map(requestDoc => 
      deleteDoc(doc(ridesRequestsCollection, requestDoc.id))
    );
    
    await Promise.all(deletePromises);
    
    console.log(`Ride ${rideId} and associated requests deleted`);
    return { success: true };
  } catch (error) {
    console.error("Error deleting ride: ", error);
    throw error;
  }
}

// Function to get ride statistics (Admin dashboard)
export async function getRideStatistics() {
  try {
    const ridesCollection = collection(firestore, "Rides");
    const ridesRequestsCollection = collection(firestore, "RidesRequests");
    
    // Get all rides
    const ridesSnapshot = await getDocs(ridesCollection);
    const totalRides = ridesSnapshot.size;
    
    // Count rides by status
    const ridesByStatus = {};
    ridesSnapshot.docs.forEach(doc => {
      const status = doc.data().rideStatus || 'unknown';
      ridesByStatus[status] = (ridesByStatus[status] || 0) + 1;
    });
    
    // Get all ride requests
    const requestsSnapshot = await getDocs(ridesRequestsCollection);
    const totalRequests = requestsSnapshot.size;
    
    // Count requests by status
    const requestsByStatus = {};
    requestsSnapshot.docs.forEach(doc => {
      const status = doc.data().status || 'unknown';
      requestsByStatus[status] = (requestsByStatus[status] || 0) + 1;
    });
    
    return {
      totalRides,
      totalRequests,
      ridesByStatus,
      requestsByStatus
    };
  } catch (error) {
    console.error("Error getting ride statistics: ", error);
    throw error;
  }
}